import {css, StyleSheet} from 'aphrodite';
import produce from 'immer';
import React from 'react';

import {RelatedResultsFormState} from 'analysis-tool/data-frame-creation/schemas/RegistrySchemaDataFrameCreationTypes';
import {getAndAssertObjectType} from 'analysis-tool/data-frame-creation/schemas/RegistrySchemasDataFrameCreationUtils';
import AddItemButton from 'analysis-tool/data-frame-creation/steps/AddItemButton';
import {EMPTY_SELECTION_SET} from 'query-api/graphql-field-selector/CompositeField';
import {GraphQLField} from 'query-api/graphql-field-selector/GraphQLFieldSelectorTypes';
import {QueryApiIntrospection, useQueryApiExtensionStatus} from 'query-api/useQueryApiIntrospection';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Tooltip from 'ui-library/bs/Tooltip';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {immutableReplace} from 'util/ImmutableListUtil';

import RelatedResultsForm from './RelatedResultsForm';

const EMPTY_FORM_STATE = {
  backlinkFieldName: null,
  backlinkSelections: EMPTY_SELECTION_SET,
  joinKeyFieldName: null,
  onlyIncludeLatestItem: true,
};

interface RelatedResultsFormListProps {
  entityListingField: GraphQLField;
  relatedResultsFormStates: Array<RelatedResultsFormState>;
  onChangeRelatedResultFormStates: (newFormStates: Array<RelatedResultsFormState>) => void;
  queryApiIntrospection: QueryApiIntrospection;
  maxResultSchemas?: number;
}

export default function RelatedResultsFormList({
  entityListingField,
  relatedResultsFormStates,
  onChangeRelatedResultFormStates,
  queryApiIntrospection,
  maxResultSchemas,
}: RelatedResultsFormListProps): JSX.Element {
  const entityType = getAndAssertObjectType(entityListingField);
  const extensionStatus = useQueryApiExtensionStatus(entityType.name);
  if (extensionStatus === AsyncStatus.ERROR) {
    return (
      <div {...getTestProps({component: 'RelatedResultsFormList', element: 'root'})}>
        <InlineBanner bannerType={InlineBannerType.ERROR} text="Failed to load related result schemas." />
      </div>
    );
  } else if (extensionStatus === AsyncStatus.LOADING || extensionStatus === AsyncStatus.NOT_FETCHED) {
    return (
      <div {...getTestProps({component: 'RelatedResultsFormList', element: 'root'})}>
        <CenteredHorizontalThrobber />
      </div>
    );
  }

  const typeHasBacklinkFields = queryApiIntrospection.typeHasResultSchemaBacklinkFields(entityType);

  function isBacklinkFieldSelected(backlinkFieldName: string): boolean {
    return relatedResultsFormStates.some(
      (relatedResultsFormState) => relatedResultsFormState.backlinkFieldName === backlinkFieldName
    );
  }

  return (
    <div {...getTestProps({component: 'RelatedResultsFormList', element: 'root'})}>
      {relatedResultsFormStates.map((relatedResultsFormState, index) => (
        <RelatedResultsForm
          // `index` is not a stable identifier and would lead to expansion state being incorrect when
          // removing result schemas earlier in the list, so use `backlinkFieldName` when possible.
          // Because of this, it is particularly important not to allow the same result schema to be selected
          // twice since it'd lead to the same key being used twice (which is partially why
          // `isBacklinkFieldSelected` exists, in addition to it being slightly better UX).
          //
          // When there is no backlinkFieldName selected (e.g. no result schema selected), there is also no
          // field selector or other state so it is ok to fall back to an unstable index in this case.
          // See: https://react.dev/learn/rendering-lists#where-to-get-your-key
          key={relatedResultsFormState.backlinkFieldName ?? index}
          entityListingField={entityListingField}
          formState={relatedResultsFormState}
          isBacklinkFieldSelected={isBacklinkFieldSelected}
          onChangeFormState={(newRelatedResultsFormState) => {
            onChangeRelatedResultFormStates(
              immutableReplace(relatedResultsFormStates, index, newRelatedResultsFormState)
            );
          }}
          onRemove={() => {
            onChangeRelatedResultFormStates(
              produce(relatedResultsFormStates, (draftFormState) => {
                draftFormState.splice(index, 1);
              })
            );
          }}
          queryApiIntrospection={queryApiIntrospection}
        />
      ))}
      {(maxResultSchemas == null || relatedResultsFormStates.length < maxResultSchemas) && (
        <div className={css(styles.addResultsButtonWrapper)}>
          <Tooltip disabled={typeHasBacklinkFields} title="No result schemas available">
            <AddItemButton
              disabled={!typeHasBacklinkFields}
              label="Add Results"
              onClick={() => {
                // TODO(BNCH-58142): Also collapse the entity + result field selector(s) above.
                // This is blocked by making GraphQLFieldSelector's expansion state controlled.
                onChangeRelatedResultFormStates([...relatedResultsFormStates, EMPTY_FORM_STATE]);
              }}
              dataTestAttrs={{component: 'RelatedResultsFormList', element: 'addItemButton'}}
            />
          </Tooltip>
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  addResultsButtonWrapper: {
    // Without this, the tooltip will be horizontally centered over the entire width of the form rather than
    // directly above the Add Results button.
    width: 'fit-content',
  },
});
